<template>
  <div>
    <!-- One block per match, laid out as the configuration looked: the header
         with the time point and its condition, read-only, and below it the
         analyses with their sample and their own due date -->
    <div
      v-for="block in blocks"
      :key="block.key"
      class="stability-summary-block"
    >
      <div class="stability-summary-header">
        <div class="stability-summary-fields">
          <!-- Sized on the token: T0 and T4weeks_40_75 both fit -->
          <el-input
            :value="block.token"
            size="mini"
            readonly
            :style="{ width: `${Math.max(block.token.length, 3) + 4}ch` }"
          />

          <span class="stability-summary-label">
            {{ $t('stability.matrix') }}
          </span>
          <el-input
            :value="block.matrix"
            size="mini"
            readonly
            class="stability-summary-matrix"
          />

          <span class="stability-summary-label">
            {{ $t('stability.temperature') }}
          </span>
          <el-input
            :value="String(block.temperature)"
            size="mini"
            readonly
            class="stability-summary-number"
          >
            <template slot="append">°C</template>
          </el-input>

          <span class="stability-summary-label">
            {{ $t('stability.humidity') }}
          </span>
          <el-switch
            :value="block.humidity != null"
            disabled
          />
          <el-input
            v-if="block.humidity != null"
            :value="String(block.humidity)"
            size="mini"
            readonly
            class="stability-summary-number"
          >
            <template slot="append">%RH</template>
          </el-input>
        </div>

        <div class="stability-summary-dates">
          <div class="stability-summary-date">
            <span class="stability-summary-label">
              {{ $t('stability.summary.fromDate') }}
            </span>
            <el-input
              :value="block.fromDate"
              size="mini"
              readonly
              prefix-icon="el-icon-date"
            />
          </div>
          <div class="stability-summary-date">
            <span class="stability-summary-label">
              {{ $t('stability.summary.toDate') }}
            </span>
            <el-input
              :value="block.toDate"
              size="mini"
              readonly
              prefix-icon="el-icon-date"
            />
          </div>
        </div>
      </div>

      <div class="stability-summary-analyses">
        <div class="stability-summary-grid stability-summary-columns">
          <span>{{ $t('stability.summary.analysis') }}</span>
          <span>{{ $t('stability.summary.labSample') }}</span>
          <span>{{ $t('stability.summary.labSampleStatus') }}</span>
          <span>{{ $t('stability.summary.analysisStatus') }}</span>
          <span>{{ $t('stability.summary.toDate') }}</span>
        </div>
        <div
          v-for="row in block.rows"
          :key="row.key"
          class="stability-summary-grid stability-summary-row"
          :class="{ 'is-child': row.isChild }"
        >
          <span class="stability-summary-analysis">
            <i class="el-icon-document" />
            {{ row.label }}
          </span>
          <span>{{ row.labSampleCode }}</span>
          <!-- Same coloured marker as the Gestione and Inserimento lists -->
          <span
            class="stability-summary-status"
            :class="
              row.statusModifier &&
              `stability-summary-status--${row.statusModifier}`
            "
          >
            {{ row.labSampleStatus }}
          </span>
          <!-- Same marker: every analysis status is also a key of $sample-statuses -->
          <span
            class="stability-summary-status"
            :class="
              row.analysisStatusModifier &&
              `stability-summary-status--${row.analysisStatusModifier}`
            "
          >
            {{ row.analysisStatus }}
          </span>
          <span>{{ row.toDate }}</span>
        </div>
        <div
          v-if="!block.rows.length"
          class="stability-summary-row"
        >
          {{ $t('stability.noAnalyses') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import {
    LAB_SAMPLE_STATUSES_DESC,
    SAMPLE_ANALYSIS_STATUSES_DESC,
  } from '@/config/constants';

  // fromDate and the match toDate are `timestamp without time zone`: the API
  // hands back the stored midnight shifted, and local time undoes it — the same
  // convention as MatchCard and TimePointSection
  const formatTimestamp = (value) =>
    value ? moment(value).format('DD/MM/YYYY') : '-';

  // The per-analysis dates are plain calendar days (`YYYY-MM-DD`)
  const formatDay = (value) =>
    value ? moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY') : '-';

  export default {
    name: 'StabilitySummary',

    props: {
      // { match, timePoint } pairs, already ordered by the page (its
      // `matchCards`): the API returns the time points in no given order, and
      // one place deciding the order is enough
      matchCards: { type: Array, required: true },
    },

    computed: {
      /**
       * One block per match. The dates are the PLANNED ones: fromDate plus the
       * lead time of each analysis.
       */
      blocks() {
        return this.matchCards.map(({ match, timePoint }) => {
          const condition = match.stabilityCondition || {};
          return {
            key: match.id,
            token: timePoint.token,
            matrix: condition.matrix || '-',
            temperature: condition.temperature,
            humidity: condition.humidity ?? null,
            fromDate: formatTimestamp(timePoint.fromDate),
            toDate: formatTimestamp(match.toDate),
            // The API order is kept: each parent followed by its children
            rows: (match.analysisDueDates || []).map((due) => {
              // Null until configured: there is no sample yet. The key names
              // both the label and the colour in $sample-statuses
              const statusModifier =
                LAB_SAMPLE_STATUSES_DESC[due.labSampleStatus] || null;
              // Null until configured as well; labelled as in Inserimento
              // dati and Gestione status
              const analysisStatusModifier =
                SAMPLE_ANALYSIS_STATUSES_DESC[due.analysisStatus] || null;
              return {
                // Not the analysis alone: a single that is also the child of
                // a fixed class, or a child shared by two classes, appears twice
                key: `${due.parentAnalysisId ?? ''}-${due.analysisId}`,
                label: `${due.analysisCode} - ${due.analysisDescription}`,
                isChild: due.parentAnalysisId != null,
                labSampleCode: due.labSampleCode || '-',
                labSampleStatus: statusModifier
                  ? this.$t(`enums.sampleStatuses.${statusModifier}`)
                  : '-',
                statusModifier,
                analysisStatus: analysisStatusModifier
                  ? this.$t(
                      `enums.labSampleAnalysisStatuses.${analysisStatusModifier}`
                    )
                  : '-',
                analysisStatusModifier,
                toDate: formatDay(due.toDate),
              };
            }),
          };
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  // The block is a panel on the page, the analyses a darker panel inside it
  .stability-summary-block {
    background-color: $--color-black-lighter;
    border: 1px solid $--color-black;
    border-radius: $--border-radius-base;
    padding: 0.75rem 0.75rem 0.5rem;
    margin-bottom: 0.75rem;
  }

  // Fields on the left, the two dates pushed to the right
  .stability-summary-header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.5rem 2rem;
    margin-bottom: 0.75rem;
  }

  .stability-summary-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
  }

  .stability-summary-label {
    color: $--color-text-regular;
    font-size: $--font-size-small;
    white-space: nowrap;
  }

  .stability-summary-matrix {
    width: 8rem;
  }

  .stability-summary-number {
    width: 8rem;
  }

  .stability-summary-dates {
    display: flex;
    gap: 2rem;
  }

  // Label above the field, as in the configuration header
  .stability-summary-date {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 11rem;
  }

  .stability-summary-analyses {
    background-color: $--color-black;
    border-radius: $--border-radius-base;
    padding: 0.5rem 0.75rem;
    font-size: $--font-size-small;
  }

  // Analysis · sample · sample status · analysis status · date, the date
  // column lined up under the header dates
  .stability-summary-grid {
    display: grid;
    grid-template-columns: 4fr 3fr 2fr 2fr 11rem;
    gap: 1rem;
    align-items: center;
  }

  .stability-summary-columns {
    color: $--color-text-regular;
    padding-bottom: 0.35rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid $--color-black-lighter;
  }

  .stability-summary-row {
    color: $--color-text-primary;
    line-height: 1.8;
  }

  .stability-summary-analysis {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;

    i {
      color: $--color-text-regular;
    }
  }

  // The marker of the Gestione and Inserimento lists (`.table-cell-status`):
  // a bar in the colour of the status on the left of the label
  .stability-summary-status {
    position: relative;
    align-self: stretch;
    display: flex;
    align-items: center;

    @each $modifier, $map in $sample-statuses {
      &--#{$modifier} {
        padding-left: 18px;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 10px;
          background: map-get($map, 'background');
        }
      }
    }
  }

  // The children of a fixed class sit under their parent
  .stability-summary-row.is-child .stability-summary-analysis {
    padding-left: 1.25rem;
  }
</style>
